import { useEffect, useState } from "react";
import { MapPin, Store, Navigation, Check, Bike } from "lucide-react";
import { api, brl, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TopBar, NotificationBell, EnableAlertsButton } from "@/components/Shell";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export default function CourierHome() {
  const { user, refresh } = useAuth();
  const [online, setOnline] = useState(!!user?.online);
  const [rides, setRides] = useState([]);
  const [active, setActive] = useState(null);
  const [known, setKnown] = useState(0);
  const [code, setCode] = useState("");

  const load = async () => {
    try {
      const [a, b] = await Promise.all([api.get("/courier/available"), api.get("/courier/active")]);
      if (a.data.length > known) { toast.success("🛵 Nova corrida disponível!"); }
      setKnown(a.data.length);
      setRides(a.data);
      setActive(b.data);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [online]);

  const toggle = async (v) => {
    setOnline(v);
    await api.put("/courier/online", { online: v });
    await refresh();
    toast.success(v ? "Você está online e recebendo corridas" : "Você está offline");
    load();
  };

  const act = async (path, id, msg, body = {}) => {
    try {
      await api.post(`/courier/orders/${id}/${path}`, body);
      toast.success(msg);
      setCode("");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="courier-home">
      <TopBar subtitle="Motoboy · Magé/RJ" right={<><EnableAlertsButton /><NotificationBell /></>} />

      <div className="px-4 pt-5">
        <div
          data-testid="online-toggle-card"
          className="rounded-3xl p-5 text-white"
          style={{ background: online ? "linear-gradient(100deg,#FF7A1A,#F7941D)" : "#141414" }}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[11px] uppercase font-bold tracking-wider text-white/80">Status</p>
              <h1 className="font-display text-2xl font-extrabold" data-testid="online-status-label">
                {online ? "Online" : "Offline"}
              </h1>
              <p className="text-xs text-white/85 mt-1">
                {online ? "Recebendo corridas na sua região" : "Ative para receber corridas"}
              </p>
            </div>
            <Switch data-testid="online-toggle" checked={online} onCheckedChange={toggle} />
          </div>
        </div>
      </div>

      {active && (
        <div className="px-4 mt-5">
          <h2 className="font-display font-bold text-lg">Corrida em andamento</h2>
          <div className="mt-2 rounded-2xl border-2 border-gf-orange p-4 bg-white" data-testid="active-ride-card">
            <p className="font-display font-extrabold">{active.code} · {brl(active.split.courier_payout)}</p>
            <div className="mt-3 space-y-2 text-xs">
              <p className="flex items-start gap-2">
                <Store size={14} className="text-gf-orange mt-0.5" />
                <span><b>Retirada:</b> {active.store_name} — {active.store_address || "Centro, Magé"}</span>
              </p>
              <p className="flex items-start gap-2">
                <MapPin size={14} className="text-mage-green mt-0.5" />
                <span><b>Entrega:</b> {active.address}</span>
              </p>
              <p className="flex items-center gap-2 text-gray-500">
                <Navigation size={14} /> Rota estimada · {active.distance_km} km
              </p>
            </div>
            <div className="mt-4 h-24 rounded-xl bg-gf-gray flex items-center justify-center text-[11px] text-gray-500">
              Mapa da rota (simulado): {active.store_name} → {active.address}
            </div>
            {active.status === "ready" ? (
              <Button
                data-testid="confirm-pickup-button"
                onClick={() => act("pickup", active.id, "Retirada confirmada!")}
                className="w-full h-11 rounded-xl mt-4 bg-gf-black text-white font-bold gf-press"
              >
                <Check size={16} className="mr-1" /> Confirmar retirada na loja
              </Button>
            ) : (
              <div className="mt-4">
                <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">
                  Código de entrega do cliente
                </p>
                <div className="flex gap-2 mt-1">
                  <Input data-testid="delivery-code-input" value={code} maxLength={4} inputMode="numeric"
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    placeholder="0000"
                    className="h-11 rounded-xl text-center font-display text-xl tracking-[0.3em]" />
                  <Button data-testid="confirm-delivery-button"
                    onClick={() => act("deliver", active.id, "Entrega confirmada!", { code })}
                    className="h-11 rounded-xl bg-mage-green text-white font-bold gf-press px-5">
                    <Check size={16} className="mr-1" /> Confirmar
                  </Button>
                </div>
                <p className="text-[11px] text-gray-500 mt-1">
                  Peça ao cliente os 4 últimos dígitos do telefone dele para concluir a entrega com segurança.
                </p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2 mt-2">
              <Button
                data-testid="cancel-ride-button"
                variant="outline"
                onClick={async () => {
                  const reason = window.prompt("Motivo do cancelamento (opcional):") ?? "";
                  try {
                    await api.post(`/orders/${active.id}/cancel`, { reason });
                    toast.success("Pedido cancelado");
                    load();
                  } catch (e) {
                    toast.error(apiError(e.response?.data?.detail));
                  }
                }}
                className="h-10 rounded-xl font-bold text-mage-red border-mage-red col-span-2"
              >
                Cancelar pedido
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="px-4 mt-6">
        <h2 className="font-display font-bold text-lg">Corridas disponíveis</h2>
        <div className="grid gap-3 mt-3 gf-stagger" data-testid="available-rides">
          {rides.map((o) => (
            <div key={o.id} className="rounded-2xl border border-gray-200 p-4 bg-white" data-testid={`ride-${o.id}`}>
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-display font-bold">{o.store_name}</p>
                  <p className="text-[11px] text-gray-500">{o.address}</p>
                </div>
                <div className="text-right">
                  <p className="font-display font-extrabold text-gf-orange">{brl(o.split.courier_payout)}</p>
                  <p className="text-[10px] text-gray-500">{o.distance_km} km</p>
                </div>
              </div>
              <div className="flex gap-2 mt-3">
                <Button
                  data-testid={`accept-ride-${o.id}`}
                  onClick={() => act("accept", o.id, "Corrida aceita!")}
                  className="flex-1 h-10 rounded-xl gf-gradient text-white font-bold gf-press"
                >
                  <Bike size={15} className="mr-1" /> Aceitar corrida
                </Button>
                <Button
                  data-testid={`reject-ride-${o.id}`}
                  variant="outline"
                  onClick={() => toast.info("Corrida recusada")}
                  className="h-10 rounded-xl font-bold text-gray-500"
                >
                  Recusar
                </Button>
              </div>
            </div>
          ))}
          {rides.length === 0 && (
            <p className="text-sm text-gray-500 py-8 text-center">
              {online ? "Nenhuma corrida disponível agora." : "Fique online para ver corridas."}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
